import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Card,
  ConfirmModal,
  Disclosure,
  Icon,
  Label,
  Modal,
  RelativeDateTime,
  SimpleSummary,
  TextInput,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';

import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import CopyInput from './CopyInput';
import {
  useIdentityGrantCreate,
  useIdentityGrantDelete,
  useIdentityGrantList,
} from './hooks/useIdentityGrants';

type StoreIdentityGrant = CrewMerchantUi.StoreIdentityGrant;
type StoreIdentityGrantWithClientSecret =
  CrewMerchantUi.StoreIdentityGrantWithClientSecret;

function FormActions({
  onCancel,
  onSubmit,
  submitLabel = 'Submit',
  submitVariant = 'primary',
  isDisabled = false,
  isLoading = false,
  showCancel = true,
}: {
  onCancel: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  submitVariant?: 'primary' | 'destructive';
  isDisabled?: boolean;
  isLoading?: boolean;
  showCancel?: boolean;
}) {
  return (
    <div className="flex flex-row justify-end gap-2">
      {showCancel && <Action onClick={onCancel}>Cancel</Action>}
      <Action
        variant={submitVariant}
        type={onSubmit ? 'button' : 'submit'}
        onClick={onSubmit}
        disabled={isDisabled}
        loading={isLoading}
      >
        {submitLabel}
      </Action>
    </div>
  );
}

function IdentityGrantItem({ grant }: { grant: StoreIdentityGrant }) {
  const { mutate: deleteGrant, isPending: isDeleting } =
    useIdentityGrantDelete();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    deleteGrant(grant.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Disclosure
        renderSummary={
          <SimpleSummary>
            <div className="flex flex-1 items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon icon="lockKey" className="text-neutral-500" />
                <span className="font-medium">{grant.name}</span>
              </div>
              <Action
                icon="remove"
                accessibilityLabel={`Delete ${grant.name}`}
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
              />
            </div>
          </SimpleSummary>
        }
      >
        <div className="flex flex-col gap-4 px-6 py-4">
          <CopyInput label="Client ID" value={grant.clientId} />
          <div className="flex flex-col gap-1.5">
            <Label>Created</Label>
            <RelativeDateTime
              dateTime={grant.createdOn}
              className="text-sm text-neutral-500"
            />
          </div>
          <p className="text-xs text-neutral-500">
            Ready to get started?{' '}
            <Action
              to="https://dev.corso.com/category/commerce-api"
              target="_blank"
              variant="link"
              rel="noreferrer"
            >
              Read the documentation
            </Action>
          </p>
        </div>
      </Disclosure>

      <ConfirmModal
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete API Token"
        prompt="Are you sure you want to delete this API token? This action cannot be undone and any applications using this token will lose access."
        confirmText="Delete"
        variant="destructive"
        loading={isDeleting}
      />
    </>
  );
}

function useIdentityGrantCreateForm() {
  return useForm({
    resolver: zodResolver(CrewMerchantUi.storeIdentityGrantCreateSchema),
    defaultValues: { name: '' },
  });
}

function IdentityGrantCreateForm({
  showForm,
  closeForm,
}: {
  showForm: boolean;
  closeForm: () => void;
}) {
  const { mutateAsync: createGrant, isPending } = useIdentityGrantCreate();
  const [createdGrant, setCreatedGrant] =
    useState<StoreIdentityGrantWithClientSecret | null>(null);
  const [hasSeenSecret, setHasSeenSecret] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
    reset,
  } = useIdentityGrantCreateForm();

  const closeHandler = () => {
    closeForm();
    reset();
    setCreatedGrant(null);
    setHasSeenSecret(false);
  };

  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(
      (data) =>
        createGrant(data)
          .then((result) => {
            setCreatedGrant(result);
            setHasSeenSecret(false);
          })
          .catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  if (createdGrant && !hasSeenSecret) {
    return (
      <Modal
        show={showForm}
        onClose={closeHandler}
        size="lg"
        title="API Token Created"
      >
        <div className="flex flex-col gap-4">
          <CopyInput label="Name" value={createdGrant.name} />
          <CopyInput label="Client ID" value={createdGrant.clientId} />
          <CopyInput label="Client Secret" value={createdGrant.clientSecret} />
        </div>

        <Alert variant="warning" heading="Copy Your Client Secret">
          This client secret will not be shown again. Make sure to copy it to a
          secure location.
        </Alert>
        <FormActions
          onCancel={closeHandler}
          onSubmit={() => {
            setHasSeenSecret(true);
            closeHandler();
          }}
          submitLabel="I've copied the Client Secret"
          showCancel={false}
        />
      </Modal>
    );
  }

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Create API Token"
      description="Create a new API token for use in your server-side applications. Give it a name to identify its purpose."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="api-token-name"
          label="Name"
          details="A name to identify this API token (e.g. Integration, Production API)"
          required
          {...register('name')}
          error={errors.name?.message}
        />

        <FormActions
          onCancel={closeHandler}
          submitLabel="Create"
          isDisabled={!isDirty || isPending}
          isLoading={isPending}
        />
      </form>
    </Modal>
  );
}

export default function StoreIdentitySettings() {
  const { data: grants = [], isLoading } = useIdentityGrantList();
  const [showForm, setShowForm] = useState(false);

  const hasStoreExceedGrantLimit = grants.length >= 4;

  return (
    <SettingsAnchorCard id="commerce-api">
      <Card.Line as="header">
        <Card.Heading as="h3">Commerce API</Card.Heading>
        {!hasStoreExceedGrantLimit && (
          <Action
            icon="add"
            onClick={() => setShowForm(true)}
            variant="ghost"
            accessibilityLabel="Add API Token"
          />
        )}
      </Card.Line>
      <Card.Description>
        Create a new API token to access the Corso Commerce API from your
        server-side applications.
      </Card.Description>

      {/* oxlint-disable-next-line no-nested-ternary */}
      {isLoading ? (
        <p className="text-sm text-neutral-400 italic">Loading...</p>
      ) : grants.length > 0 ? (
        <div className="flex flex-col gap-2">
          {grants.map((grant) => (
            <IdentityGrantItem key={String(grant.id)} grant={grant} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 italic">
          No API tokens configured yet.
        </p>
      )}

      <IdentityGrantCreateForm
        showForm={showForm}
        closeForm={() => setShowForm(false)}
      />
    </SettingsAnchorCard>
  );
}
